import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, App, Button, Card, Empty, Flex, Form, Input, Modal, Radio, Skeleton, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { PlatformLabel } from "../channels/PlatformBadge";
import { MappedTo, SkuSelect, useRefreshMapping, type PlatformSKU } from "./mapping";

type Group = { key: string; reasons: ("same_code" | "similar_title")[]; members: PlatformSKU[] };
// choice: "sku:<id>" (an internal SKU a member already sells), "other" or "new".
type Target = { choice: string; otherSkuId?: string; code: string; name: string };

/** Tinjau duplikat (App Flow §7, PRD-3): merge or dismiss each group. */
export function Duplicates() {
  const { t, formatMoney } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefreshMapping();
  const manage = can("catalog.manage");
  const [merging, setMerging] = useState<Group | null>(null);
  useTabTitle(t("catalog.dup.title"));

  const list = useQuery({ queryKey: ["duplicates"], queryFn: () => unwrap(api.GET("/v1/catalog/duplicates")) });
  const dismiss = useMutation({
    mutationFn: (key: string) => unwrap(api.POST("/v1/catalog/duplicates/dismiss", { body: { key } })),
    onSuccess: () => {
      void message.success(t("catalog.dup.dismissed"));
      refresh();
    },
    onError: (err) => {
      void message.error(errorText(err));
      refresh();
    },
  });

  const groups = (list.data?.groups ?? []) as Group[];
  return (
    <>
      <Typography.Title level={2} style={{ marginTop: 0, fontSize: 28 }}>
        {t("catalog.dup.title")}
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ maxWidth: 760 }}>
        {t("catalog.dup.intro")}
      </Typography.Paragraph>
      {list.isPending ? (
        <Skeleton active />
      ) : groups.length === 0 ? (
        <Empty description={t("catalog.dup.empty")} />
      ) : (
        <Space orientation="vertical" size={16} style={{ width: "100%" }}>
          {(list.data?.total ?? 0) > groups.length && <Alert type="info" showIcon title={t("catalog.dup.more", { shown: groups.length, total: list.data!.total })} />}
          {groups.map((g) => (
            <Card
              key={g.key}
              size="small"
              title={
                <Space wrap>
                  {g.reasons.map((r) => (
                    <Tag key={r} color={r === "same_code" ? "warning" : "processing"}>
                      {t(`catalog.dup.reason.${r}`)}
                    </Tag>
                  ))}
                  <Typography.Text>{g.members[0]?.listingName}</Typography.Text>
                </Space>
              }
              extra={
                manage && (
                  <Space>
                    <Button loading={dismiss.isPending && dismiss.variables === g.key} onClick={() => dismiss.mutate(g.key)}>
                      {t("catalog.dup.dismiss")}
                    </Button>
                    <Button type="primary" onClick={() => setMerging(g)}>
                      {t("catalog.dup.merge")}
                    </Button>
                  </Space>
                )
              }
            >
              <Table<PlatformSKU>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={g.members}
                scroll={{ x: 760 }}
                columns={[
                  {
                    title: t("orders.store"),
                    width: 200,
                    render: (_, p) => (
                      <Space orientation="vertical" size={0}>
                        <PlatformLabel platform={p.platform} />
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {p.shopName}
                        </Typography.Text>
                      </Space>
                    ),
                  },
                  { title: t("catalog.platform.product"), render: (_, p) => [p.listingName, p.variant].filter(Boolean).join(" · ") },
                  { title: t("catalog.platform.sellerSku"), dataIndex: "sellerSku", width: 150, render: (v: string) => <span className="z-num">{v || "—"}</span> },
                  { title: t("catalog.platform.price"), dataIndex: "price", width: 120, align: "right", render: (v: number | null) => <span className="z-num">{v === null ? "—" : formatMoney(v, "IDR")}</span> },
                  { title: t("catalog.platform.mappedTo"), width: 200, render: (_, p) => (p.mappedTo ? <MappedTo p={p} /> : <Typography.Text type="secondary">{t("catalog.dup.notMapped")}</Typography.Text>) },
                ]}
              />
            </Card>
          ))}
        </Space>
      )}
      <MergeModal group={merging} onClose={() => setMerging(null)} />
    </>
  );
}

/** The most common non-empty value, for the new SKU's suggested code. */
function mostCommon(values: string[]): string {
  const n = new Map<string, number>();
  for (const v of values.filter(Boolean)) n.set(v, (n.get(v) ?? 0) + 1);
  return [...n.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
}

function MergeModal({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const refresh = useRefreshMapping();
  const [form] = Form.useForm<Target>();
  const choice = Form.useWatch("choice", form);

  // Internal SKUs the members already sell: the natural targets.
  const existing = group ? [...new Map(group.members.filter((m) => m.mappedTo).map((m) => [m.mappedTo!.id, m.mappedTo!])).values()] : [];
  const first = group?.members[0];
  const initial: Target = {
    choice: existing[0] ? `sku:${existing[0].id}` : "new",
    code: mostCommon(group?.members.map((m) => m.sellerSku) ?? []),
    name: first ? [first.listingName, first.variant].filter(Boolean).join(" - ") : "",
  };

  const merge = useMutation({
    mutationFn: (v: Target) => {
      const target =
        v.choice === "new"
          ? { create: { code: v.code.trim(), name: v.name.trim() } }
          : { skuId: v.choice === "other" ? v.otherSkuId! : v.choice.slice("sku:".length) };
      return unwrap(api.POST("/v1/catalog/duplicates/merge", { body: { key: group!.key, ...target } }));
    },
    onSuccess: () => {
      void message.success(t("catalog.dup.mergedPlain"));
      refresh();
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
      refresh();
    },
  });

  return (
    <Modal
      open={group !== null}
      title={t("catalog.dup.mergeTitle", { n: group?.members.length ?? 0 })}
      okText={t("catalog.dup.merge")}
      cancelText={t("common.cancel")}
      confirmLoading={merge.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={initial} onFinish={(v) => merge.mutate({ ...initial, ...v })}>
        <Form.Item name="choice" label={t("catalog.dup.target")}>
          <Radio.Group
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
            options={[
              ...existing.map((e) => ({
                value: `sku:${e.id}`,
                label: (
                  <span>
                    <span className="z-num">{e.code}</span> · {e.name}
                  </span>
                ),
              })),
              { value: "other", label: t("catalog.dup.targetOther") },
              { value: "new", label: t("catalog.dup.targetNew") },
            ]}
          />
        </Form.Item>
        {choice === "other" && (
          <Form.Item name="otherSkuId" rules={[{ required: true, message: t("field.required") }]}>
            <SkuSelect autoFocus />
          </Form.Item>
        )}
        {choice === "new" && (
          <Flex gap={12}>
            <Form.Item name="code" label={t("catalog.code")} style={{ width: 200 }} rules={[{ required: true, message: t("field.required") }, { pattern: /^\S{1,64}$/, message: t("field.invalid") }]}>
              <Input className="z-num" maxLength={64} />
            </Form.Item>
            <Form.Item name="name" label={t("common.name")} style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: t("field.required") }, { max: 255, message: t("field.too_long") }]}>
              <Input />
            </Form.Item>
          </Flex>
        )}
      </Form>
    </Modal>
  );
}
